import { ProductCategory as ProductCategoryEnum, SessionStorageKeys } from '@aglenergy/shared-library';
import { store } from 'app/shared/store/store';
import { AddressDetailModel } from 'app/shared/models/address/address.model';
import { ProductCatalogueProduct } from 'app/shared/models/product-catalogue-product.model';
import { tealiumAnalyticService } from 'app/shared/services/tealium/tealium-analytics.service';
import { CustomerProfile } from 'app/shared/models/customerProfile';
import { customerProfileService } from 'app/shared/services/customer-profile/customerProfile-service';
import { bundleService } from 'app/shared/services//bundle/bundle.service';
import { crossSellService } from 'app/shared/services/cross-sell/cross-sell.service';
import { NbnProductsResponse } from 'app/nbn/models/nbn-products-response.model';
import { SelectedProductSessionData } from 'app/shared/models/sessionData';
import { addressService } from 'app/shared/services/address/address.service';
import { couponCodeService } from 'app/shared/services/coupon-code/coupon-code.service';
import { locationService } from 'app/shared/services/location/location-service';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';

class ProductService {
  public onSelectedProductForNbn(
    products: ProductCatalogueProduct[],
    selectedAddress: AddressDetailModel,
    sku: string,
    eligibilityPath?: string
  ): void {
    let isValid = true;
    let errors: Error[] = [] as Error[];

    if (!selectedAddress) {
      errors = [...errors, { name: 'address', message: 'No address' }];
      isValid = false;
      tealiumAnalyticService.trackCheckYourAddressButton('Check your address', ProductCategoryEnum.Nbn);
    }

    store.set('validationErrors', errors);

    if (isValid) {
      const customerProfile: CustomerProfile = customerProfileService.getCustomerProfile();
      if (bundleService.isExplicitBundleFlow()) {
        customerProfile.isBundleExplicitlyRequestedByCustomer = true;
      } else if (crossSellService.isCrossSellFlowAvailableOnAddress(selectedAddress.fullAddress)) {
        customerProfile.isBundleExplicitlyRequestedByCustomer = false;
      }
      customerProfile.customerOriginURL = window.location.href;
      customerProfile.customerType = 'R';
      customerProfileService.setCustomerProfile(customerProfile);

      const selectedProduct = products.find((x) => x.sku === sku);
      const productDetail = {
        product: { ...selectedProduct, webId: store.get(StoreKeys.webId) ? store.get(StoreKeys.webId) : null },
        serviceQualification: (store.get('productDetails') as NbnProductsResponse)?.serviceQualification
      };

      const selectedProductSessionDataJSON: SelectedProductSessionData = {
        address: selectedAddress,
        addressString: addressService.convertAddressToString(selectedAddress),
        productDetails: [productDetail],
        customerProfile
      };

      const couponCode = store.get('couponCode');
      const guidedFlow = store.get('isGuidedFlow');
      if (couponCode) {
        couponCodeService.setSessionStorage(couponCode);
      } else if (store.get('couponCodes').find((c) => c.productCategory === 'nbn')) {
        couponCodeService.setMultipleCouponSessionStorage(store.get('couponCodes').find((c) => c.productCategory === 'nbn'));
      } else if (!guidedFlow) {
        couponCodeService.resetSessionStorage();
      }

      window.sessionStorage.setItem(SessionStorageKeys.SelectedNBNProductDetails, JSON.stringify(selectedProductSessionDataJSON));

      tealiumAnalyticService.trackPlanSelectionData(selectedProduct, ProductCategoryEnum.Nbn, 'Choose this plan');

      locationService.navigateToEligibility(
        'nbn',
        null,
        eligibilityPath,
        eligibilityPath === 'productswap' || store.get(StoreKeys.isCampaignPage)
      );
    }
  }

  public onSelectedProductForMobile(sku: string, products: ProductCatalogueProduct[], isGuidedFlow: boolean): void {
    let selectedProductSessionData = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.SelectedMobileProductDetails));
    const selectedProduct = products.find((x) => x.sku === sku);
    const customerProfile: CustomerProfile = customerProfileService.getCustomerProfile();
    customerProfile.customerOriginURL = window.location.href;
    customerProfile.customerType = 'R';
    customerProfileService.setCustomerProfile(customerProfile);

    if (selectedProductSessionData) {
      selectedProductSessionData.productDetails.push({
        product: { ...selectedProduct, webId: store.get(StoreKeys.webId) ? store.get(StoreKeys.webId) : null }
      });
    } else {
      selectedProductSessionData = {
        productDetails: [
          {
            product: { ...selectedProduct, webId: store.get(StoreKeys.webId) ? store.get(StoreKeys.webId) : null }
          }
        ],
        customerProfile
      };
    }

    if (crossSellService.isCrossSellFlow()) {
      customerProfile.isBundleExplicitlyRequestedByCustomer = false;
      customerProfileService.setCustomerProfile(customerProfile);
    }

    window.sessionStorage.setItem(SessionStorageKeys.SelectedMobileProductDetails, JSON.stringify(selectedProductSessionData));

    const couponCode = store.get('couponCode');
    if (couponCode) {
      couponCodeService.setSessionStorage(couponCode);
    } else if (store.get('couponCodes').find((c) => c.productCategory === 'mobile plan')) {
      couponCodeService.setMultipleCouponSessionStorage(store.get('couponCodes')?.find((c) => c.productCategory === 'mobile plan'));
    } else if (!isGuidedFlow) {
      couponCodeService.resetSessionStorage();
    }

    tealiumAnalyticService.trackPlanSelectionData(selectedProduct, 'mobile plan', 'Choose this plan');
    locationService.navigateToEligibility('mobile plan', null, null, store.get(StoreKeys.isCampaignPage));
  }

  public onSelectedMobileProductForChangePlan(products: ProductCatalogueProduct[], sku: string, eligibilityPath: string) {
    const customerProfile: CustomerProfile = customerProfileService.getCustomerProfile();
    const productDetails = products.find((p) => p.sku === sku);
    if (customerProfile && productDetails) {
      const selectedProduct: any = {
        customerProfile,
        productDetails: [{ product: productDetails }]
      };

      sessionStorage.setItem(SessionStorageKeys.SelectedMobileProductDetails, JSON.stringify(selectedProduct));
      tealiumAnalyticService.trackPlanSelectionData(selectedProduct, 'mobile plan', 'Choose this plan');
      locationService.navigateToEligibility('mobile plan', null, eligibilityPath, eligibilityPath === 'productswap', 'pre-cart');
    }
  }
}

export const productService = new ProductService();
